import { useState, useEffect, useMemo, useCallback, useContext } from 'react'
import { useSelector } from 'react-redux'
import { loadTossPayments } from '@tosspayments/payment-sdk'

import { AnimatedWindowCallbackContext } from 'context/window/AnimatedWindowProvider'
import { CenterModalCallbackContext } from 'context/popup/CenterModalProvider'
import { ToastCallbackContext } from 'context/popup/ToastProvider'

import server from 'server/serverController2'
import constants, { SHIPPING_EXCEPTIONS, SHIPPING_ACTION_ARR } from 'utils/constants'

import ShippingAddressForm from 'components/containers/commerce/order/ShippingAddressForm'
import ShippingList from 'components/presenters/commerce/order/shippingList/ShippingList'
import ConfirmCenterModal from 'components/presenters/popup/ConfirmCenterModal'

const useProductOrder = ({ selectedOptions, purchaseType }) => {
  const userInfo = useSelector((state) => state.userInfo)
  const { productInfo, shippingInfo } = useSelector((state) => state.productDetailInfo)

  const { openWindow, closeWindow, id } = useContext(AnimatedWindowCallbackContext)
  const { openCenterModal, closeCenterModal } = useContext(CenterModalCallbackContext)
  const { openToast } = useContext(ToastCallbackContext)

  const [shippingList, setShippingList] = useState([])
  const [currShipping, setCurrShipping] = useState(null)
  const [shippingDetail, setShippingDetail] = useState('')
  const [isValid, setIsValid] = useState(false)
  const [shippingPrice, setShippingPrice] = useState(0)
  const [totalChallengePrice, setTotalChallengePrice] = useState(0)
  const [subTotalPrice, setSubTotalPrice] = useState(0)
  const [totalPrice, setTotalPrice] = useState(0)
  const [serviceAgreement, setServiceAgreement] = useState(false)

  const loadUserShipping = useMemo(async () => {
    try {
      if (userInfo?.isLogin) {
        const response = await server.getRequestData(`users/shippings`)
        if (response.success !== 1) {
          const errData = response.error.res_data
          throw errData.detail_code
        }

        const { shippings } = response.data

        return shippings
      }
    } catch (error) {
      alert('유저 배송지 정보를 읽어오지 못했어요.')
    }
  }, [userInfo?.isLogin])

  const handleChangeShippingDetail = useCallback((data) => {
    const { label } = data

    if (label === '직접 입력') {
      setCurrShipping((shipping) => ({ ...shipping, shipping_action: '' }))
    } else {
      setCurrShipping((shipping) => ({ ...shipping, shipping_action: label }))
    }
    setShippingDetail(label)
  }, [])

  const handleChangeShippingDetailByEtc = useCallback((e) => {
    const { value } = e.target
    setShippingDetail(value)
    setCurrShipping((shipping) => ({ ...shipping, shipping_action: value }))
  }, [])

  const getShippingDetail = useCallback((action) => {
    if (!SHIPPING_ACTION_ARR.includes(action)) {
      return '직접 입력'
    } else {
      return action
    }
  }, [])

  const handleSetCurrShipping = useCallback(
    (shipping) => {
      if (shipping.is_default) {
        if (shipping.shipping_action) {
          const shippingAction = getShippingDetail(shipping.shipping_action)
          setShippingDetail(shippingAction)
        }
        setCurrShipping(shipping)
      }
    },
    [getShippingDetail],
  )

  const handleSetUserShipping = useCallback(async () => {
    const userShippings = await loadUserShipping
    setShippingList(userShippings)

    if (userShippings.length > 0) {
      for (const shipping of userShippings) {
        handleSetCurrShipping(shipping)
      }
    }
  }, [loadUserShipping, handleSetCurrShipping])

  const handleChangePaymentTermsAgree = useCallback((e) => {
    const { checked } = e.target

    setServiceAgreement(checked)
  }, [])

  const handleCompleteAddShippingAddress = useCallback(
    (userShipping) => {
      handleSetCurrShipping(userShipping)
      setShippingList((shippingList) => [...shippingList, userShipping])
      closeWindow(id)
    },
    [closeWindow, handleSetCurrShipping, id],
  )

  const handleCompleteChangeShippingAddress = useCallback(
    (userShipping) => {
      if (userShipping.shipping_action) {
        const shippingAction = getShippingDetail(userShipping.shipping_action)
        setShippingDetail(shippingAction)
      }
      setCurrShipping(userShipping)
      closeWindow(id)
      openToast({ iconName: 'checkCircle', desc: '배송지 변경이 완료되었어요', delay: 2000 })
    },
    [closeWindow, getShippingDetail, id, openToast],
  )

  const openShippingAddressForm = () => {
    openWindow({
      id,
      Component: <ShippingAddressForm onComplete={handleCompleteAddShippingAddress} />,
      headerProps: { title: '배송지 추가' },
    })
  }

  const openShippingChangeWindow = () => {
    openWindow({
      id,
      Component: (
        <ShippingList
          onComplete={handleCompleteChangeShippingAddress}
          shippingList={shippingList}
        />
      ),
      headerProps: {
        title: '배송지',
      },
    })
  }

  const openAgreementAlertModal = () => {
    const dataList = {
      title: '결제 약관 동의',
      description: `결제 약관 동의에\n체크해주세요`,
    }
    openCenterModal({
      Component: (
        <ConfirmCenterModal dataList={dataList} onComplete={(id) => closeCenterModal(id)} />
      ),
    })
  }

  const handlePayment = useCallback(async () => {
    try {
      let totalQuantity = 0
      const body = {
        order_shipping: currShipping,
        subtotal: subTotalPrice,
        total_shipping: shippingPrice,
        total_price: totalPrice,
        total_challenge_price: totalChallengePrice,
        order_items: selectedOptions.map((option) => {
          totalQuantity += option.count
          return {
            product_sku_no: option.no,
            unit_price: option.price,
            challenge_unit_price: option.challengePrice,
            quantity: option.count,
            is_challenge: option.isChallenge,
          }
        }),
      }

      const response = await server.postRequestData('commerce/orders', body)
      if (response.success !== 1) {
        const errData = response.error.res_data
        throw errData.detail_code
      }

      const { order } = response.data

      const tossPayments = await loadTossPayments(constants.tossClientKey)
      tossPayments.requestPayment('CARD', {
        successUrl: `${constants.appUrl}/motishop/order/${order.no}/approve?purchase_type=${purchaseType}&challenge_no=${productInfo.challenge_info.no}`,
        failUrl: `${constants.appUrl}/motishop/order/${order.no}/deny?product_no=${productInfo.no}`,
        orderId: `${order.toss_order_id}`,
        orderName: `${productInfo.title} * ${totalQuantity}`,
        customerName: userInfo.name,
        amount: totalPrice,
      })
    } catch (error) {
      const dataList = {}
      if (error === 'NOT_ENOUGH_STOCK_EXIST') {
        dataList.title = '결제 실패'
        dataList.description = '재고 수량이 부족해요.\n나중에 다시 시도해주세요.'
      } else {
        dataList.title = '결제 실패'
        dataList.description = '결제에 실패했어요\nadmin@unitmi.com 으로 문의해주세요.'
      }
      openCenterModal({
        Component: (
          <ConfirmCenterModal dataList={dataList} onComplete={(id) => closeCenterModal(id)} />
        ),
      })
    }
  }, [
    closeCenterModal,
    currShipping,
    openCenterModal,
    productInfo.challenge_info.no,
    productInfo.no,
    productInfo.title,
    purchaseType,
    selectedOptions,
    shippingPrice,
    subTotalPrice,
    totalChallengePrice,
    totalPrice,
    userInfo.name,
  ])

  const calcOrderPrice = useCallback(() => {
    let shippingPrice = shippingInfo.price
    const shippingExceptions = SHIPPING_EXCEPTIONS
    for (const shippingException of shippingExceptions) {
      if (
        shippingException.zipcodeStart <= currShipping?.zipcode &&
        currShipping?.zipcode <= shippingException.zipcodeEnd
      ) {
        if (shippingException.type === 'jeju') {
          shippingPrice += shippingInfo.jeju_price
        } else {
          shippingPrice += shippingInfo.other_price
        }
        break
      }
    }

    let subTotalPrice = 0
    let challengePrice = 0
    for (const option of selectedOptions) {
      subTotalPrice += option.count * option.price
      challengePrice +=
        option.isChallenge === true
          ? option.count * option.challengePrice
          : option.count * option.price
    }

    return { shippingPrice, subTotalPrice, challengePrice }
  }, [
    currShipping?.zipcode,
    selectedOptions,
    shippingInfo.jeju_price,
    shippingInfo.other_price,
    shippingInfo.price,
  ])

  useEffect(() => {
    handleSetUserShipping()
  }, [handleSetUserShipping])

  useEffect(() => {
    const { shippingPrice, subTotalPrice, challengePrice } = calcOrderPrice()

    setShippingPrice(shippingPrice)
    setSubTotalPrice(subTotalPrice)
    setTotalChallengePrice(challengePrice)
    setTotalPrice(shippingPrice + subTotalPrice)
  }, [currShipping, calcOrderPrice])

  useEffect(() => {
    if (serviceAgreement === true && currShipping !== null) {
      setIsValid(true)
    } else {
      setIsValid(false)
    }
  }, [currShipping, serviceAgreement])

  return {
    currShipping,
    shippingDetail,
    shippingPrice,
    subTotalPrice,
    totalChallengePrice,
    totalPrice,
    serviceAgreement,
    isValid,
    handleChangeShippingDetail,
    handleChangeShippingDetailByEtc,
    openShippingAddressForm,
    openShippingChangeWindow,
    openAgreementAlertModal,
    handleChangePaymentTermsAgree,
    handlePayment,
  }
}

export default useProductOrder
